import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

export const AcceptInvitation = graphql(/* GraphQL */ `
  mutation AcceptInvitation($input: AcceptInvitationInput!) {
    acceptInvitation(input: $input) {
      __typename
      ... on InvitationEmailMismatchError {
        message
      }
      ... on EmailAlreadyMemberError {
        message
      }
      ... on TwoFactorSetupRequiredError {
        message
      }
    }
  }
`)

export type AcceptInvitationOutcome =
  | { kind: 'success' }
  | {
      kind:
        | 'expired'
        | 'emailMismatch'
        | 'alreadyMember'
        | 'network'
        | 'twoFactorRequired'
        | 'unknown'
      message: string
    }

interface UseAcceptInvitationReturn {
  acceptInvitation: (input: { jwt: string }) => Promise<AcceptInvitationOutcome>
  loading: boolean
}

/**
 * Expired / not-found / forged-token cases are no longer typed errors
 * on the `AcceptInvitationPayload` union — the backend surfaces them via
 * `data.errors` (a thrown GraphQL error), so they land in the `catch`
 * block below. Only the user-actionable cases (email mismatch, already
 * a member, 2FA setup required by the inviting org) remain typed. We map
 * the generic GraphQL error message back to an `expired` outcome since
 * that's the dominant cause for a thrown invitation error and drives the
 * "expired or revoked" banner.
 * Apollo `networkError`s (offline/timeout) are split out as `network` so
 * a transient connection failure doesn't read as an invalid invitation.
 */
export const useAcceptInvitation = (): UseAcceptInvitationReturn => {
  const [mutate, { loading }] = useMutation(AcceptInvitation)

  const acceptInvitation = async (input: {
    jwt: string
  }): Promise<AcceptInvitationOutcome> => {
    try {
      const { data } = await mutate({ variables: { input } })

      const result = data?.acceptInvitation
      if (!result) {
        return { kind: 'unknown', message: 'Failed to accept invitation' }
      }

      switch (result.__typename) {
        case 'EmptySuccess':
          return { kind: 'success' }
        case 'InvitationEmailMismatchError':
          return { kind: 'emailMismatch', message: result.message }
        case 'EmailAlreadyMemberError':
          return { kind: 'alreadyMember', message: result.message }
        case 'TwoFactorSetupRequiredError':
          return { kind: 'twoFactorRequired', message: result.message }
        default:
          return { kind: 'unknown', message: 'Unknown error occurred' }
      }
    } catch (err) {
      // A transport failure (offline, timeout, non-2xx) isn't an invalid
      // invitation — surface it as `network` so the UI can offer a retry
      // instead of the misleading "expired or revoked" banner. Anything that
      // isn't a GraphQL error from the server is treated as a transport issue.
      if (!CombinedGraphQLErrors.is(err)) {
        return {
          kind: 'network',
          message:
            'Could not reach the server. Check your connection and try again.',
        }
      }
      // Expired / revoked / forged tokens come back as thrown GraphQL
      // errors now — treat them as "expired" for the UI banner.
      return {
        kind: 'expired',
        message:
          err instanceof Error
            ? err.message
            : 'This invitation has expired or been revoked.',
      }
    }
  }

  return { acceptInvitation, loading }
}
